import { Box } from '@mui/material';

import type {
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent
} from 'react';

import { stopPropagation } from '@/utils/stopPropagation';

import { MAX_DATA_COLUMN_WIDTH, MIN_DATA_COLUMN_WIDTH } from './constants';

type Props = {
  columnWidth: number;
  label: string;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  onDoubleClick: (event: ReactMouseEvent) => void;
};

const ResizeHandle = ({ columnWidth, label, onPointerDown, onKeyDown, onDoubleClick }: Props) => (
  <Box
    aria-label={`Resize ${label}`}
    aria-orientation='vertical'
    aria-valuemax={MAX_DATA_COLUMN_WIDTH}
    aria-valuemin={MIN_DATA_COLUMN_WIDTH}
    aria-valuenow={columnWidth}
    role='separator'
    tabIndex={0}
    sx={{
      alignItems: 'stretch',
      bottom: 0,
      cursor: 'col-resize',
      display: 'flex',
      justifyContent: 'flex-end',
      position: 'absolute',
      right: -12,
      top: 0,
      touchAction: 'none',
      width: 12,
      zIndex: 1,
      '&:focus-visible': {
        outline: 'none'
      },
      '& > .resize-grip': {
        backgroundColor: 'divider',
        borderRadius: 1,
        margin: '10px 0',
        opacity: 0.7,
        transform: 'scaleX(1)',
        transformOrigin: 'center',
        transition: 'background-color 120ms ease, opacity 120ms ease, transform 120ms ease',
        width: 2
      },
      '&:hover > .resize-grip, &:focus-visible > .resize-grip': {
        backgroundColor: 'primary.main',
        opacity: 1,
        transform: 'scaleX(1.5)'
      },
      '&:active > .resize-grip': {
        backgroundColor: 'primary.main',
        opacity: 1,
        transform: 'scaleX(1.5)'
      }
    }}
    onClick={stopPropagation}
    onDoubleClick={onDoubleClick}
    onKeyDown={onKeyDown}
    onPointerDown={onPointerDown}
  >
    <Box
      aria-hidden='true'
      className='resize-grip'
    />
  </Box>
);

export default ResizeHandle;
